<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="visible" class="gbm-overlay" @click.self="close">
        <div class="gbm-modal" @click.stop>

          <!-- Header -->
          <div class="gbm-header">
            <div class="gbm-header-badge">
              <i class="fas fa-building"></i>
            </div>
            <div class="gbm-header-text">
              <h2>Đặt xe theo tổ chức / Doanh nghiệp</h2>
              <p>Xe riêng từ 16 chỗ – BusDCX lo trọn gói từ A đến Z</p>
            </div>
            <button class="gbm-close" @click="close" aria-label="Đóng">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Success state -->
          <div v-if="submitSuccess" class="gbm-success">
            <div class="gbm-success-check">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3>Yêu cầu đã được gửi!</h3>
            <p>Chúng tôi sẽ liên hệ bạn sớm nhất qua<br><strong>{{ form.leader_email }}</strong> hoặc <strong>{{ form.leader_phone }}</strong></p>
            <button class="gbm-success-btn" @click="close">Đóng</button>
          </div>

          <!-- Form -->
          <form v-else class="gbm-form" @submit.prevent="handleSubmit">

            <!-- Section 1: Representative & Org -->
            <div class="gbm-section">
              <div class="gbm-section-label">
                <span class="gbm-step">1</span>
                Thông tin người đại diện
              </div>

              <div class="gbm-field">
                <label>Tên công ty / tổ chức <span class="req">*</span></label>
                <div class="gbm-input-icon">
                  <i class="fas fa-building"></i>
                  <input v-model="form.organization_name" type="text" placeholder="VD: Công ty TNHH ABC, Trường THPT XYZ..." required />
                </div>
              </div>

              <div class="gbm-grid-2">
                <div class="gbm-field">
                  <label>Người đại diện <span class="req">*</span></label>
                  <input v-model="form.leader_name" type="text" placeholder="Nguyễn Văn A" required />
                </div>
                <div class="gbm-field">
                  <label>Số điện thoại <span class="req">*</span></label>
                  <input
                    v-model="form.leader_phone"
                    type="tel"
                    placeholder="0903..."
                    required
                    :class="{ 'has-error': errors.phone }"
                  />
                  <span v-if="errors.phone" class="gbm-error-msg">{{ errors.phone }}</span>
                </div>
              </div>

              <div class="gbm-field">
                <label>Email <span class="req">*</span></label>
                <div class="gbm-input-icon">
                  <i class="fas fa-envelope"></i>
                  <input
                    v-model="form.leader_email"
                    type="email"
                    placeholder="email@company.com"
                    required
                    :class="{ 'has-error': errors.email }"
                  />
                </div>
                <span v-if="errors.email" class="gbm-error-msg">{{ errors.email }}</span>
                <span class="gbm-hint">Báo giá &amp; xác nhận sẽ được gửi qua email này</span>
              </div>
            </div>

            <div class="gbm-divider"></div>

            <!-- Section 2: Trip -->
            <div class="gbm-section">
              <div class="gbm-section-label">
                <span class="gbm-step">2</span>
                Thông tin chuyến đi
              </div>

              <div class="gbm-grid-2">
                <div class="gbm-field">
                  <label>Ngày đi <span class="req">*</span></label>
                  <div class="gbm-input-icon">
                    <i class="fas fa-calendar-alt"></i>
                    <input v-model="form.travel_date" type="date" :min="minDate" required />
                  </div>
                </div>
                <div class="gbm-field">
                  <label>Số lượng người (chỗ ngồi) <span class="req">*</span></label>
                  <div class="gbm-input-icon">
                    <i class="fas fa-users"></i>
                    <input
                      v-model.number="form.member_count"
                      type="number"
                      min="16"
                      max="500"
                      placeholder="VD: 30"
                      required
                      :class="{ 'has-error': errors.memberCount }"
                    />
                  </div>
                  <span v-if="errors.memberCount" class="gbm-error-msg">{{ errors.memberCount }}</span>
                  <span v-else class="gbm-hint">Tối thiểu 16 chỗ để đặt xe riêng tổ chức</span>
                </div>
              </div>

              <div class="gbm-field">
                <label>Địa điểm <span class="req">*</span></label>
                <div class="gbm-input-icon">
                  <i class="fas fa-map-marker-alt"></i>
                  <select v-model="form.route_id" required>
                    <option value="" disabled>Chọn địa điểm...</option>
                    <option v-for="r in availableRoutes" :key="r.route_id" :value="r.route_id">
                      {{ r.destination_location }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="gbm-divider"></div>

            <!-- Section 3: Notes -->
            <div class="gbm-section">
              <div class="gbm-section-label">
                <span class="gbm-step">3</span>
                Ghi chú thêm <span class="optional">(không bắt buộc)</span>
              </div>
              <div class="gbm-field">
                <textarea
                  v-model="form.notes"
                  placeholder="VD: Xe đón tại văn phòng, yêu cầu xe limousine, hành lý nhiều, đặc biệt cần ghế VIP..."
                  rows="3"
                ></textarea>
              </div>
            </div>

            <!-- Error -->
            <div v-if="submitError" class="gbm-submit-error">
              <i class="fas fa-exclamation-circle"></i>
              {{ submitError }}
            </div>

            <!-- Submit -->
            <button type="submit" class="gbm-submit" :disabled="isSubmitting">
              <span v-if="isSubmitting"><i class="fas fa-spinner fa-spin"></i> Đang gửi...</span>
              <span v-else><i class="fas fa-paper-plane"></i> Gửi yêu cầu ngay</span>
            </button>
          </form>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import axiosInstance from '@/plugins/axios'

export default {
  name: 'GroupBookingModal',
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ['close'],
  data () {
    return {
      form: {
        leader_name: '',
        leader_phone: '',
        leader_email: '',
        organization_name: '',
        travel_date: '',
        route_id: '',
        member_count: null,
        notes: '',
        extra_notes: '',
        additional_services: {}
      },
      errors: { phone: '', email: '', memberCount: '' },
      availableRoutes: [],
      isSubmitting: false,
      submitSuccess: false,
      submitError: ''
    }
  },
  computed: {
    minDate () {
      const now = new Date()
      now.setDate(now.getDate() + 1)
      return now.toISOString().split('T')[0]
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.fetchRoutes()
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  methods: {
    close () {
      this.$emit('close')
      setTimeout(() => { this.resetForm() }, 300)
    },
    resetForm () {
      this.form = {
        leader_name: '', leader_phone: '', leader_email: '',
        organization_name: '',
        travel_date: '', route_id: '', member_count: null,
        notes: '', extra_notes: '', additional_services: {}
      }
      this.errors = { phone: '', email: '', memberCount: '' }
      this.submitSuccess = false
      this.submitError = ''
    },
    async fetchRoutes () {
      try {
        const response = await axiosInstance.get('/routes/')
        const seen = new Set()
        this.availableRoutes = response.data.filter(r => {
          if (seen.has(r.destination_location)) return false
          seen.add(r.destination_location)
          return true
        })
      } catch (err) {
        console.error('Error fetching routes:', err)
      }
    },
    validateForm () {
      let valid = true
      this.errors = { phone: '', email: '', memberCount: '' }
      const phoneRegex = /^(0|\+84)\d{9,10}$/
      if (!phoneRegex.test(this.form.leader_phone.replace(/\s/g, ''))) {
        this.errors.phone = 'Số điện thoại không hợp lệ'
        valid = false
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.form.leader_email)) {
        this.errors.email = 'Email không hợp lệ'
        valid = false
      }
      if (!this.form.member_count || this.form.member_count < 16) {
        this.errors.memberCount = 'Đặt xe tổ chức yêu cầu tối thiểu 16 chỗ ngồi'
        valid = false
      }
      return valid
    },
    async handleSubmit () {
      if (!this.validateForm()) return
      this.isSubmitting = true
      this.submitError = ''
      try {
        const payload = {
          leader_name: this.form.leader_name,
          leader_phone: this.form.leader_phone.replace(/\s/g, ''),
          leader_email: this.form.leader_email,
          organization_name: this.form.organization_name,
          travel_date: this.form.travel_date,
          route_id: this.form.route_id || null,
          member_count: this.form.member_count,
          notes: this.form.notes || null,
          extra_notes: null,
          additional_services: {}
        }
        await axiosInstance.post('/group-booking-requests/', payload)
        this.submitSuccess = true
      } catch (err) {
        const detail = err.response?.data?.detail
        if (typeof detail === 'string') this.submitError = detail
        else if (Array.isArray(detail)) this.submitError = detail.map(d => d.msg).join(', ')
        else this.submitError = 'Có lỗi xảy ra, vui lòng thử lại'
      } finally {
        this.isSubmitting = false
      }
    }
  },
  beforeUnmount () {
    document.body.style.overflow = ''
  }
}
</script>

<style lang="scss" scoped>
/* ─── Overlay ─────────────────────── */
.gbm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
}

/* ─── Modal ────────────────────────── */
.gbm-modal {
  background: #fff;
  border-radius: 1.25rem;
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0,0,0,0.08);
  animation: gbm-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-sizing: border-box;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 4px; }
}

@keyframes gbm-in {
  from { opacity: 0; transform: scale(0.94) translateY(14px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

/* ─── Header ───────────────────────── */
.gbm-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem 1.25rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 2;
  border-radius: 1.25rem 1.25rem 0 0;
  box-sizing: border-box;
}

.gbm-header-badge {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(22, 163, 74, 0.35);

  i {
    color: white;
    font-size: 1rem;
  }
}

.gbm-header-text {
  flex: 1;
  min-width: 0;

  h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #064e3b;
    margin: 0 0 0.15rem;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  p {
    font-size: 0.78rem;
    color: #718096;
    margin: 0;
  }
}

.gbm-close {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  border: none;
  background: #edf2f7;
  color: #718096;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  i { font-size: 0.85rem; }

  &:hover {
    background: #fed7d7;
    color: #e53e3e;
    transform: rotate(90deg);
  }
}

/* ─── Form ─────────────────────────── */
.gbm-form {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  box-sizing: border-box;
}

.gbm-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e5e7eb 30%, #e5e7eb 70%, transparent);
  margin: 1.125rem 0;
  flex-shrink: 0;
}

.gbm-section {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.gbm-section-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #075985;
  text-transform: uppercase;
  letter-spacing: 0.07em;

  .optional {
    font-weight: 400;
    color: #a0aec0;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.72rem;
  }
}

.gbm-step {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #16a34a, #15803d);
  color: white;
  font-size: 0.68rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 2-column grid - responsive */
.gbm-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.gbm-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;

  label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #075985;
    .req { color: #e53e3e; }
  }
}

/* Input with icon */
.gbm-input-icon {
  position: relative;

  i {
    position: absolute;
    left: 0.8rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 0.78rem;
    pointer-events: none;
    z-index: 1;
  }

  input, select {
    padding-left: 2.2rem !important;
  }
}

/* Base inputs */
.gbm-field input,
.gbm-field select,
.gbm-field textarea {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #064e3b;
  background: #f7fafc;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  outline: none;
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    border-color: #15803d;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(56, 161, 105, 0.15);
  }

  &::placeholder { color: #a0aec0; }

  &.has-error {
    border-color: #fc8181;
    background: #fff5f5;
  }
}

.gbm-field select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath d='M6 8L1 3h10z' fill='%239CA3AF'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.875rem center;
  background-color: #f9fafb;
  padding-right: 2rem;
}

.gbm-field textarea {
  resize: vertical;
  min-height: 80px;
  line-height: 1.55;
}

.gbm-error-msg {
  font-size: 0.72rem;
  color: #ef4444;
  margin-top: -0.1rem;
}

.gbm-hint {
  font-size: 0.71rem;
  color: #9ca3af;
  margin-top: -0.1rem;
}

/* ─── Submit Error ──────────────────── */
.gbm-submit-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.625rem;
  color: #dc2626;
  font-size: 0.82rem;
  margin-top: 1rem;
}

/* ─── Submit Button ─────────────────── */
.gbm-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem;
  margin-top: 1.25rem;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white;
  border: none;
  border-radius: 0.625rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
  letter-spacing: 0.01em;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(22, 163, 74, 0.4);
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
}

/* ─── Success ───────────────────────── */
.gbm-success {
  padding: 2.5rem 1.5rem 2rem;
  text-align: center;

  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #064e3b;
    margin: 1.25rem 0 0.5rem;
  }
  p {
    font-size: 0.85rem;
    color: #6b7280;
    line-height: 1.7;
  }
}

.gbm-success-check {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
  animation: gbm-bounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes gbm-bounce {
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}

.gbm-success-btn {
  margin-top: 1.5rem;
  padding: 0.7rem 2rem;
  background: #16a34a;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: #15803d; }
}

/* ─── Transitions ───────────────────── */
.modal-fade-enter-active { transition: opacity 0.25s ease; }
.modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from,
.modal-fade-leave-to { opacity: 0; }

/* ─── Responsive ────────────────────── */
@media (max-width: 600px) {
  .gbm-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .gbm-modal {
    max-height: 94vh;
    border-radius: 1.25rem 1.25rem 0 0;
    width: 100%;
  }

  .gbm-header {
    padding: 1rem 1rem 0.875rem;
  }

  .gbm-form {
    padding: 1rem;
  }

  .gbm-grid-2 {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .gbm-header-text h2 {
    font-size: 0.92rem;
  }
}
</style>
